import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookingStepper, TripSummaryCard } from '../components/quote-v2';
import { ExtraCard, ExtrasPriceSummary, MobileExtrasSummary } from '../components/extras';
import { 
  getCompatibleOptionalExtras, 
  getCompatibleAutoIncludedExtras,
  calculateExtrasTotal, 
  validateExtraInput,
  buildExtrasDataForAPI 
} from '../config/extrasConfig';
import { getVehicleType } from '../config/vehicleTypes';
import useClarityPageReady from '../hooks/useClarityPageReady';
import { ArrowLeft, Info, Loader2, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

// Format date from raw strings
const formatDateForDisplay = (dateStr, timeStr) => {
  if (!dateStr) return 'Date not set';
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const monthName = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${weekday}, ${day} ${monthName} ${year} at ${timeStr || '00:00'}`;
  } catch (e) {
    return `${dateStr} ${timeStr || ''}`;
  }
};

const ExtrasPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Clarity page ready signal
  useClarityPageReady('extras_selection');
  
  // Session state
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Selected extras state
  const [selectedExtras, setSelectedExtras] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [quantities, setQuantities] = useState({});
  const [errors, setErrors] = useState({});

  // Get session ID from URL
  const sessionId = searchParams.get('sid') || searchParams.get('session_id');

  // Fetch session data
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/booking/${sessionId}`);
      const result = await res.json();
      
      if (result.success && result.session) {
        setSessionData(result.session);
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Get trip details from session or URL params
  const tripDetails = useMemo(() => {
    if (sessionData) {
      const date = sessionData.trip_date || '';
      const time = sessionData.trip_time || '';
      return {
        pickup: sessionData.pickup_location || 'Istanbul Airport',
        dropoff: sessionData.dropoff_location || '',
        date: date,
        time: time,
        formattedDate: formatDateForDisplay(date, time),
        passengers: sessionData.passengers || 1,
        luggage: sessionData.luggage || 1,
        distance: '45',
        durationMinutes: '55',
        timezone: sessionData.timezone || 'Europe/Istanbul',
        pickupType: sessionData.pickup_type || 'manual',
      };
    }
    // Fallback to URL params
    return {
      pickup: searchParams.get('pickup') || 'Istanbul Airport (IST)',
      dropoff: searchParams.get('dropoff') || 'Taksim Square',
      date: searchParams.get('date') || '',
      time: searchParams.get('time') || '',
      formattedDate: '',
      passengers: parseInt(searchParams.get('passengers')) || 1,
      luggage: parseInt(searchParams.get('luggage')) || 1,
      distance: '45',
      durationMinutes: '55',
      pickupType: 'manual',
    };
  }, [sessionData, searchParams]);


  // Get vehicle details from session or URL params
  const vehicleDetails = useMemo(() => {
    // While loading, return null to show loading state
    if (isLoading) {
      return null;
    }
    
    if (sessionData) {
      // Check for vehicle data in session (saved by Quote page)
      const vehicleSnapshot = sessionData.selected_vehicle_snapshot || sessionData.selected_vehicle;
      
      // Priority: session.vehicle_price > session.subtotal > snapshot price
      let vehiclePrice = sessionData.vehicle_price;
      if (!vehiclePrice && vehiclePrice !== 0) {
        vehiclePrice = sessionData.subtotal;
      }
      if (!vehiclePrice && vehiclePrice !== 0 && vehicleSnapshot) {
        vehiclePrice = vehicleSnapshot.price;
      }
      
      // Ensure it's a number
      vehiclePrice = parseFloat(vehiclePrice) || 0;
      
      if (vehicleSnapshot || vehiclePrice > 0) {
        return {
          id: vehicleSnapshot?.type || vehicleSnapshot?.vehicleType || vehicleSnapshot?.id || 'VAN',
          name: sessionData.vehicle_name || vehicleSnapshot?.name || vehicleSnapshot?.categoryDisplayName || 'VIP Van',
          price: vehiclePrice,
        };
      }
    }
    // Fallback to URL params only if session loaded but has no data
    return {
      id: searchParams.get('vehicleType') || 'VAN',
      name: searchParams.get('vehicle') || 'VIP Van',
      price: parseFloat(searchParams.get('price')) || 0,
    };
  }, [sessionData, searchParams, isLoading]);

  // Get compatible extras for selected vehicle - separate optional and auto-included
  const optionalExtras = useMemo(() => {
    return getCompatibleOptionalExtras(vehicleDetails?.id || 'VAN');
  }, [vehicleDetails?.id]);

  // Auto-included extras (Meet & Greet) - always shown
  const autoIncludedExtras = useMemo(() => {
    return getCompatibleAutoIncludedExtras(vehicleDetails?.id || 'VAN');
  }, [vehicleDetails?.id]);

  // Calculate total extras price
  const extrasTotal = useMemo(() => {
    const selected = Object.values(selectedExtras).map(extra => ({
      ...extra,
      quantity: quantities[extra.id] || 1,
    }));
    return calculateExtrasTotal(selected);
  }, [selectedExtras, quantities]);

  // Total price
  const totalPrice = (vehicleDetails?.price || 0) + extrasTotal;

  // Handle extra toggle
  const handleToggleExtra = (extra) => {
    setSelectedExtras(prev => {
      if (prev[extra.id]) {
        // Remove extra
        const { [extra.id]: removed, ...rest } = prev;
        // Clear input and error
        setInputValues(iv => {
          const { [extra.id]: _, ...rest } = iv;
          return rest;
        });
        setQuantities(q => {
          const { [extra.id]: _, ...rest } = q;
          return rest;
        });
        setErrors(e => {
          const { [extra.id]: _, ...rest } = e;
          return rest;
        });
        return rest;
      } else {
        // Add extra
        setQuantities(q => ({ ...q, [extra.id]: 1 }));
        return { ...prev, [extra.id]: extra };
      }
    });
  };

  // Handle input change
  const handleInputChange = (extraId, value) => {
    setInputValues(prev => ({ ...prev, [extraId]: value }));
    // Clear error on change
    if (errors[extraId]) {
      setErrors(prev => ({ ...prev, [extraId]: null }));
    }
  };

  // Handle quantity change
  const handleQuantityChange = (extraId, quantity) => {
    setQuantities(prev => ({ ...prev, [extraId]: quantity }));
  };

  // Validate all selected extras
  const validateExtras = () => {
    let isValid = true;
    const newErrors = {};

    Object.values(selectedExtras).forEach(extra => {
      const validation = validateExtraInput(extra, inputValues[extra.id]);
      if (!validation.valid) {
        isValid = false;
        newErrors[extra.id] = validation.message;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Processing state for API call
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle continue
  const handleContinue = async () => {
    if (!validateExtras()) {
      toast.error('Please complete all required fields', {
        style: { background: '#FF8C00', color: 'white', border: 'none' }
      });
      return;
    }

    // Build extras data including auto-included (Meet & Greet as gift)
    const extrasData = buildExtrasDataForAPI(
      Object.values(selectedExtras),
      inputValues,
      quantities
    );

    setIsProcessing(true);

    try {
      // Save extras to backend session FIRST
      const response = await fetch(`${API_BASE}/api/booking/extras`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          extras: extrasData
        })
      });

      const data = await response.json();

      if (data.success) {
        // Navigate to Details page (Step 3) with extras in URL for display
        const params = new URLSearchParams({
          sid: sessionId,
          extrasTotal: extrasTotal.toString(),
          extras: JSON.stringify(extrasData),
        });
        
        navigate(`/details?${params.toString()}`);
      } else {
        throw new Error(data.detail || 'Failed to save extras');
      }
    } catch (error) {
      console.error('Save extras error:', error);
      toast.error(error.message || 'Failed to save extras. Please try again.', {
        style: { background: '#FF8C00', color: 'white', border: 'none' }
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle back
  const handleBack = () => {
    navigate(-1);
  };

  // Get selected extras as array
  const selectedExtrasArray = useMemo(() => {
    return Object.values(selectedExtras).map(extra => ({
      ...extra,
      quantity: quantities[extra.id] || 1,
    }));
  }, [selectedExtras, quantities]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* noindex for booking flow pages */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Header />

      <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={handleBack}
              className="text-white border-white/30 hover:bg-white/10 hover:text-white bg-white/5"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Vehicle
            </Button>
          </div>

          {/* Booking Stepper - Step 2 Active */}
          <BookingStepper currentStep={2} />

          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">Select Extras</h1>
            <p className="text-gray-400">Enhance your transfer with optional services</p>
          </div>

          {/* Main 3-Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Trip Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <TripSummaryCard tripDetails={tripDetails} />
              </div>
            </div>

            {/* Center: Extras Selection */}
            <div className="lg:col-span-6 space-y-4">
              {/* Included Services Banner */}
              {autoIncludedExtras.length > 0 && (
                <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Gift className="h-5 w-5 text-green-400" />
                    <h2 className="text-lg font-semibold text-green-400">Included Services</h2>
                  </div>
                  <p className="text-sm text-gray-300 mb-4">
                    These services are included with your booking at no extra cost.
                  </p>
                  
                  {/* Auto-included Extras Cards */}
                  <div className="space-y-3">
                    {autoIncludedExtras.map((extra) => (
                      <ExtraCard
                        key={extra.id}
                        extra={extra}
                        isSelected={true}
                        onToggle={() => {}}
                        inputValue={inputValues[extra.id]}
                        onInputChange={handleInputChange}
                        quantity={1}
                        onQuantityChange={() => {}}
                        error={errors[extra.id]}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Info Banner */}
              <div className="bg-white/5 rounded-lg p-4 flex items-start gap-3">
                <Info className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-300">
                    All extras below are optional. Your booking is complete without them.
                  </p>
                </div>
              </div>

              {/* Optional Extras Header */}
              {optionalExtras.length > 0 && (
                <h2 className="text-lg font-semibold text-white mt-4">Optional Extras</h2>
              )}

              {/* Optional Extras Cards */}
              {optionalExtras.map((extra) => (
                <ExtraCard
                  key={extra.id}
                  extra={extra}
                  isSelected={!!selectedExtras[extra.id]}
                  onToggle={handleToggleExtra}
                  inputValue={inputValues[extra.id]}
                  onInputChange={handleInputChange}
                  quantity={quantities[extra.id] || 1}
                  onQuantityChange={handleQuantityChange}
                  error={errors[extra.id]}
                />
              ))}

              {/* No extras message */}
              {optionalExtras.length === 0 && autoIncludedExtras.length === 0 && (
                <div className="bg-white rounded-xl p-8 text-center">
                  <p className="text-gray-500">No extras available for this vehicle.</p>
                </div>
              )}
            </div>

            {/* Right: Price Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <ExtrasPriceSummary
                  tripAmount={vehicleDetails?.price || 0}
                  extrasAmount={extrasTotal}
                  selectedVehicle={vehicleDetails}
                  selectedExtras={selectedExtrasArray}
                  autoIncludedExtras={autoIncludedExtras}
                  onBack={handleBack}
                  onContinue={handleContinue}
                  isProcessing={isProcessing}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Price Summary */}
      <MobileExtrasSummary
        totalAmount={totalPrice}
        extrasCount={selectedExtrasArray.length}
        onBack={handleBack}
        onContinue={handleContinue}
        canContinue={true}
        isProcessing={isProcessing}
      />

      <Footer />
    </div>
  );
};

export default ExtrasPage;
